import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import ContentRow from '../components/ContentRow';
import { Play, Plus, Share2, Check, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { getActiveProfileId } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [inList, setInList] = useState(false);
  const [loading, setLoading] = useState(true);
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/contents/${id}`, { withCredentials: true });
        setContent(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const toggleList = async () => {
    try {
      if (inList) {
        await axios.delete(`${API}/watchlist/${id}`, { params: { profile_id: getActiveProfileId() }, withCredentials: true });
        setInList(false);
      } else {
        await axios.post(`${API}/watchlist/${id}`, { profile_id: getActiveProfileId() }, { withCredentials: true });
        setInList(true);
      }
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
        <div className="w-10 h-10 border-[3px] border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
        <Header />
        <p className="text-white/60 mt-24">Contenu introuvable</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }} data-testid="content-detail-page">
      <Header />

      {/* Hero */}
      <div className="relative w-full h-[60vh] md:h-[75vh]">
        <img
          src={content.banner_url || content.thumbnail_url}
          alt={content.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#11041F] via-[#11041F]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#11041F]/80 via-transparent to-transparent" />

        {/* Back btn */}
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="absolute top-20 left-6 md:left-12 z-20 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
        </button>

        {/* Content info */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-16 pb-12 z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] tracking-tighter text-white mb-4" data-testid="detail-title">
            {content.title}
          </h1>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-sm font-semibold text-white/80">{content.year}</span>
            <span className="px-2 py-0.5 bg-white/20 rounded text-xs font-semibold backdrop-blur-sm">{content.rating}</span>
            {content.duration && <span className="text-sm text-white/60">{content.duration}</span>}
            <span className="text-sm text-white/60 capitalize">{content.type === 'series' ? 'Série' : 'Film'}</span>
            {content.tags?.map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-[#E9641A]/20 text-[#E9641A] rounded text-xs font-medium">{tag}</span>
            ))}
          </div>

          <div className="mb-6 max-w-2xl">
            <p className={`text-sm md:text-base text-white/70 leading-relaxed ${content.description?.length > 200 && !descExpanded ? 'line-clamp-3' : ''}`} data-testid="detail-description">
              {content.description}
            </p>
            {content.description?.length > 200 && (
              <button onClick={() => setDescExpanded(v => !v)} data-testid="detail-desc-toggle"
                className="flex items-center gap-1 text-xs font-semibold text-[#E9641A] hover:text-[#FE975E] mt-1.5 transition-colors">
                {descExpanded ? <>Moins <ChevronUp size={13} /></> : <>Plus <ChevronDown size={13} /></>}
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="detail-play-btn"
              onClick={() => navigate(`/watch/${content.id}`)}
              className="flex items-center gap-2 bg-white text-black font-semibold px-8 py-3 rounded-md hover:bg-white/85 transition-all"
            >
              <Play size={20} fill="currentColor" />
              Regarder
            </button>
            <button
              data-testid="detail-list-btn"
              onClick={toggleList}
              className="flex items-center gap-2 bg-white/15 text-white font-semibold px-6 py-3 rounded-md backdrop-blur-md hover:bg-white/25 transition-all"
            >
              {inList ? <Check size={20} /> : <Plus size={20} />}
              {inList ? 'Dans ma liste' : 'Ma liste'}
            </button>
            <button
              data-testid="detail-share-btn"
              onClick={() => navigator.clipboard?.writeText(window.location.href)}
              className="w-11 h-11 rounded-full border border-white/30 flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-all"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Episodes if series */}
      {content.type === 'series' && content.episodes?.length > 0 && (
        <div className="px-6 md:px-16 py-10" data-testid="episodes-section">
          <h2 className="text-2xl font-semibold font-['Outfit'] text-white mb-6">Épisodes</h2>
          <div className="space-y-3">
            {content.episodes.map((ep, idx) => (
              <div
                key={ep.id}
                data-testid={`episode-${idx}`}
                onClick={() => navigate(`/watch/${content.id}?episode=${ep.id}`)}
                className="flex items-center gap-4 p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer group"
              >
                <span className="text-2xl font-light text-white/30 w-8 text-center">{idx + 1}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{ep.title || `Épisode ${ep.episode}`}</p>
                  <p className="text-xs text-white/40 mt-0.5">{ep.duration || ''}</p>
                </div>
                <Play size={18} className="text-white/30 group-hover:text-white transition-colors" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {content.recommendations?.length > 0 && (
        <div className="px-6 md:px-16 pb-20">
          <ContentRow title="Recommandations" items={content.recommendations} />
        </div>
      )}
    </div>
  );
}
